/**
 * The ONE persistence hook every generation flow uses.
 *
 * A flow hands over a single `state` object plus the list of which keys hold
 * image payloads. The hook decides *where* each piece lives - the flow never
 * does:
 *   - non-image keys -> localStorage (`flow-state:<slug>`), `{savedAt}`+TTL,
 *     shared across tabs (survives the email-confirm new tab + browser restart)
 *   - image keys     -> IndexedDB via `flowImageStore` + `flowImageCodecs`
 *     (no ~5 MB quota cliff; the actual File/Blob bytes are kept)
 *
 * Restore-once-on-mount merges both halves into a single `onRestore` call.
 * `clearFlowState(key)` (called by every flow on success / upload-removal)
 * transparently wipes BOTH stores - so no flow gains a new clear call.
 *
 * --- Adding a future flow (the whole recipe) ---
 *   1. Add one slug to FLOW_KEYS (src/config/flowKeys.ts).
 *   2. In the flow's hook:
 *        useFlowPersistence({
 *          key: FLOW_KEYS.MY_NEW_FLOW,
 *          state: { prompt, ratio, upload },   // config + image fields mixed
 *          imageKeys: ["upload"],              // whichever are images
 *          onRestore: (s) => { ...setState(s) },
 *        });
 *   3. Nothing else. Clearing (existing clearFlowState call), sign-out wipe,
 *      24h TTL, multi-tab restore and blob-URL cleanup are all automatic.
 * A new image *shape* (rare) is a one-place addition in flowImageCodecs.ts.
 */

import { useEffect, useRef } from "react";
import {
  putFlowImages,
  getFlowImages,
  clearFlowImages,
  type FlowImageEntry,
} from "@/lib/flowImageStore";
import {
  encodeImageField,
  decodeImageField,
  type CodecName,
} from "@/lib/flowImageCodecs";

const STORAGE_PREFIX = "flow-state:";
const RECORD_VERSION = 1;

/** Drafts older than this are discarded on restore. */
export const FLOW_STATE_TTL_MS = 24 * 60 * 60 * 1000;

interface ImageMeta {
  codec: CodecName;
  meta: unknown;
}

interface StoredRecord {
  v: number;
  savedAt: number;
  /** JSON-safe non-image state. */
  data: Record<string, unknown>;
  /** Per-image-key codec + JSON remainder; binary lives in IndexedDB. */
  images: Record<string, ImageMeta>;
}

function readRecord(key: string): StoredRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const rec = JSON.parse(raw) as StoredRecord;
    if (rec.v !== RECORD_VERSION) return null;
    if (Date.now() - rec.savedAt > FLOW_STATE_TTL_MS) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

function writeRecord(
  key: string,
  data: Record<string, unknown>,
  images: Record<string, ImageMeta>,
): void {
  const record: StoredRecord = {
    v: RECORD_VERSION,
    savedAt: Date.now(),
    data,
    images,
  };
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(record));
  } catch {
    // QuotaExceeded / private mode - this draft just won't persist. We never
    // write a partial record (data is always the live non-image state), so a
    // failed write loses nothing that a successful prior write had.
  }
}

/** Live non-image state -> the JSON `data` half of the record. */
function buildData(
  state: Record<string, unknown>,
  imageKeys: readonly string[],
): Record<string, unknown> {
  const imgSet = new Set(imageKeys);
  const data: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(state)) {
    if (!imgSet.has(k)) data[k] = v;
  }
  return data;
}

/**
 * Back-compat: returns just the JSON config portion (the original
 * `readFlowState` contract). Image payloads are restored via the hook.
 */
export function readFlowState<T extends Record<string, unknown>>(
  key: string,
): T | null {
  const rec = readRecord(key);
  return rec ? (rec.data as T) : null;
}

/** Drop a flow's draft from BOTH stores. Safe to call fire-and-forget. */
export function clearFlowState(key: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // private-mode / quota edge - safe to ignore.
  }
  void clearFlowImages(key);
}

/** Slot id namespacing keeps one image key's entries from colliding with
 *  another's inside the single per-flow IndexedDB bucket. `imageKeys` are
 *  fixed lowercase identifiers and slot ids are `[a-z0-9-_]`+timestamps, so
 *  neither contains `/` and splitting on the first `/` is unambiguous. */
const slot = (imageKey: string, id: string) => `${imageKey}/${id}`;
const splitSlot = (s: string): [string, string] => {
  const i = s.indexOf("/");
  return [s.slice(0, i), s.slice(i + 1)];
};

/** Cheap fingerprint of the image-bearing values so the IndexedDB write only
 *  fires when an image actually changes (not on every prompt keystroke). */
function imageSignature(
  imageKeys: readonly string[],
  state: Record<string, unknown>,
): string {
  const parts: string[] = [];
  for (const k of imageKeys) {
    const v = state[k];
    if (v == null) {
      parts.push(`${k}:none`);
    } else if (v instanceof File) {
      parts.push(`${k}:f:${v.name}:${v.size}:${v.lastModified}`);
    } else if (v instanceof Blob) {
      parts.push(`${k}:b:${v.size}:${v.type}`);
    } else if (typeof v === "string") {
      parts.push(`${k}:s:${v.length}:${v.slice(0, 24)}`);
    } else if (Array.isArray(v)) {
      parts.push(
        `${k}:a:${v
          .map((it) =>
            it && typeof it === "object"
              ? `${(it as { id?: string }).id ?? ""}/${
                  (it as { file?: File }).file?.size ??
                  ((it as { url?: string }).url ?? "").length
                }`
              : String(it),
          )
          .join(",")}`,
      );
    } else if (typeof v === "object") {
      const o = v as { id?: string; file?: File; publicUrl?: string };
      parts.push(`${k}:o:${o.id ?? ""}:${o.file?.size ?? o.publicUrl ?? ""}`);
    }
  }
  return parts.join("|");
}

/** Pull every `blob:` URL out of a restored value so we can revoke on unmount.
 *  Only the UploadedSource codecs mint blob URLs (data-URL codecs return
 *  `data:` strings, which need no revocation). */
function collectBlobUrls(value: unknown): string[] {
  const out: string[] = [];
  const visit = (v: unknown) => {
    if (!v) return;
    if (Array.isArray(v)) return v.forEach(visit);
    if (typeof v === "object") {
      const p = (v as { previewUrl?: unknown }).previewUrl;
      if (typeof p === "string" && p.startsWith("blob:")) out.push(p);
    }
  };
  visit(value);
  return out;
}

interface UseFlowPersistenceOptions {
  /** Per-flow slug from FLOW_KEYS. */
  key: string;
  /** Everything to persist - config and image fields together. */
  state: Record<string, unknown>;
  /** Which `state` keys hold image payloads (File/Blob/UploadedSource/data
   *  URL/{id,url}[]). Omit for config-only flows. */
  imageKeys?: readonly string[];
  /** Called once on mount with the merged restored value (images rehydrated
   *  in their original shape). */
  onRestore?: (restored: Record<string, unknown>) => void;
  /** Skip persistence entirely. */
  enabled?: boolean;
}

export function useFlowPersistence({
  key,
  state,
  imageKeys = [],
  onRestore,
  enabled = true,
}: UseFlowPersistenceOptions): void {
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const imageKeysRef = useRef(imageKeys);
  imageKeysRef.current = imageKeys;

  const hasRestoredRef = useRef(false);
  const lastImageSigRef = useRef<string | null>(null);
  // Latest image meta. Both save effects compose the full record from live
  // non-image state + this ref, so neither reads the other's localStorage
  // write back (no clobber, no per-render JSON.parse, no `data:{}` path).
  const lastImagesMetaRef = useRef<Record<string, ImageMeta>>({});
  const blobUrlsRef = useRef<string[]>([]);

  // -- Restore once on mount --
  useEffect(() => {
    if (hasRestoredRef.current) return;
    let cancelled = false;

    (async () => {
      const rec = readRecord(key);
      const restored: Record<string, unknown> = rec ? { ...rec.data } : {};

      if (rec && Object.keys(rec.images).length > 0) {
        const stored = await getFlowImages(key);
        const byKey = new Map<string, FlowImageEntry[]>();
        for (const e of stored ?? []) {
          const [imgKey, id] = splitSlot(e.id);
          const arr = byKey.get(imgKey) ?? [];
          arr.push({ id, file: e.file });
          byKey.set(imgKey, arr);
        }
        for (const [imgKey, { codec, meta }] of Object.entries(rec.images)) {
          const value = await decodeImageField(
            codec,
            byKey.get(imgKey) ?? [],
            meta,
          );
          if (value !== undefined) restored[imgKey] = value;
        }
      }

      if (cancelled) return;
      hasRestoredRef.current = true;
      if (rec) {
        // Prime the signature with the RESTORED image values so the
        // post-restore re-render doesn't immediately rewrite the same bytes.
        // When nothing was restored, leave it null so the first real image
        // the user adds is persisted.
        lastImageSigRef.current = imageSignature(
          imageKeysRef.current,
          restored,
        );
        // Preserve the restored image meta until an actual image change
        // recomputes it (so config-only saves don't blank it).
        lastImagesMetaRef.current = rec.images ?? {};
        blobUrlsRef.current = imageKeysRef.current.flatMap((k) =>
          collectBlobUrls(restored[k]),
        );
        onRestoreRef.current?.(restored);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Restore-once-on-mount is intentional: only `key` should re-trigger it.
  }, [key]);

  // Revoke restore-minted blob URLs on unmount.
  useEffect(
    () => () => {
      if (typeof URL.revokeObjectURL !== "function") return;
      for (const u of blobUrlsRef.current) URL.revokeObjectURL(u);
    },
    [],
  );

  // -- Save JSON config on change (after restore) --
  // `data` is always the live non-image state; `images` is whatever the image
  // effect last computed (ref). Never reads localStorage, never writes a
  // partial record.
  useEffect(() => {
    if (!enabled || !hasRestoredRef.current) return;
    writeRecord(
      key,
      buildData(state, imageKeysRef.current),
      lastImagesMetaRef.current,
    );
  }, [key, state, enabled]);

  // -- Save images to IndexedDB only when an image actually changes --
  useEffect(() => {
    if (!enabled || !hasRestoredRef.current) return;
    if (imageKeysRef.current.length === 0) return;
    const sig = imageSignature(imageKeysRef.current, state);
    if (sig === lastImageSigRef.current) return;
    lastImageSigRef.current = sig;

    const entries: FlowImageEntry[] = [];
    const images: Record<string, ImageMeta> = {};
    for (const k of imageKeysRef.current) {
      let enc: ReturnType<typeof encodeImageField>;
      try {
        enc = encodeImageField(state[k]);
      } catch (err) {
        // Corrupt stored image (e.g. truncated base64 from localStorage) must
        // never bubble up and crash the route. Skip that one key and continue.
        console.warn("useFlowPersistence: skipping unencodable image key", k, err);
        continue;
      }
      if (!enc) continue;
      images[k] = { codec: enc.codec, meta: enc.meta };
      for (const e of enc.entries) {
        entries.push({ id: slot(k, e.id), file: e.file });
      }
    }

    void putFlowImages(key, entries);
    lastImagesMetaRef.current = images;
    writeRecord(key, buildData(state, imageKeysRef.current), images);
  }, [key, state, enabled]);
}
